import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  useTheme,
  Drawer,
  Paper,
  Chip,
  alpha,
} from '@mui/material';
import { Videocam as CctvIcon } from '@mui/icons-material';
import { DRAWER_WIDTH } from '../types';

interface RoiWorkLeftSidebarProps {
  leftDrawerOpen: boolean;
  roiFiles: string[];
  selectedRoiFile: string;
  onRoiFileSelect: (fileName: string) => void;
  cctvList: string[];
  selectedCctv: string;
  onCctvSelect: (cctvId: string) => void;
  cctvWithChanges: Set<string>;
}

export const RoiWorkLeftSidebar: React.FC<RoiWorkLeftSidebarProps> = ({
  leftDrawerOpen,
  roiFiles,
  selectedRoiFile,
  onRoiFileSelect,
  cctvList,
  selectedCctv,
  onCctvSelect,
  cctvWithChanges,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={leftDrawerOpen}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          position: 'relative',
          height: '100%',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          color: 'text.primary'
        },
      }}
    >
      <Box sx={{ overflow: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            pb: 1,
            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.primary.main, 0.03),
            borderRadius: 0
          }}
        >
          <Typography variant="overline" sx={{
            color: 'primary.main',
            fontWeight: 700,
            letterSpacing: 1
          }}>
            ROI FILES
          </Typography>
        </Paper>
        <List dense disablePadding>
          {roiFiles.map(f => (
            <ListItemButton
              key={f}
              selected={selectedRoiFile === f}
              onClick={() => onRoiFileSelect(f)}
              className={selectedRoiFile === f ? 'animate-fade-in' : ''}
              sx={{
                pl: 3,
                borderLeft: selectedRoiFile === f ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.15)
                  }
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.05)
                }
              }}
            >
              <ListItemText
                primary={f}
                primaryTypographyProps={{
                  fontSize: 13,
                  color: selectedRoiFile === f ? 'primary.main' : 'text.secondary',
                  fontWeight: selectedRoiFile === f ? 600 : 400
                }}
              />
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Paper
          elevation={0}
          sx={{
            p: 2,
            pb: 1,
            bgcolor: isDark ? alpha(theme.palette.secondary.main, 0.05) : alpha(theme.palette.secondary.main, 0.03),
            borderRadius: 0
          }}
        >
          <Typography variant="overline" sx={{
            color: 'secondary.main',
            fontWeight: 700,
            letterSpacing: 1
          }}>
            CCTV CAMERAS
          </Typography>
        </Paper>
        <List dense disablePadding>
          {cctvList.map(id => (
            <ListItemButton
              key={id}
              selected={selectedCctv === id}
              onClick={() => onCctvSelect(id)}
              disabled={!selectedRoiFile}
              className={selectedCctv === id ? 'animate-fade-in' : ''}
              sx={{
                pl: 3,
                borderLeft: selectedCctv === id ? `3px solid ${theme.palette.secondary.main}` : '3px solid transparent',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.secondary.main, 0.15)
                  }
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.05)
                },
                '&.Mui-disabled': {
                  opacity: 0.5
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CctvIcon
                  fontSize="small"
                  sx={{
                    color: selectedCctv === id ? 'secondary.main' : 'text.disabled',
                    transition: 'color 0.2s'
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={id}
                primaryTypographyProps={{
                  fontSize: 13,
                  color: selectedCctv === id ? 'secondary.main' : 'text.secondary',
                  fontWeight: selectedCctv === id ? 600 : 400
                }}
              />
              {cctvWithChanges.has(id) && (
                <Chip
                  label="변경됨"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.warning.main, 0.15),
                    color: 'warning.main',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    '& .MuiChip-label': {
                      px: 0.75,
                      py: 0,
                    },
                  }}
                />
              )}
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};
